import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchApertura,
  setAperturaFilters,
  clearAperturaFilters,
} from "../../../slices/biSlice";
import usePolling from "../../../hooks/usePolling";
import FilterBar from "../../../components/BI/FilterBar";
import DataTable from "../../../components/BI/DataTable";
import ExportButtons from "../../../components/BI/ExportButtons";
import { formatColones, formatPorcentaje } from "../../../utils/biCalculations";

const baseUrl = process.env.REACT_APP_API_URL;

// Opciones de agrupación disponibles
const AGRUPACION_OPTIONS = [
  { value: "provincia", label: "Provincia" },
  { value: "canton", label: "Cantón" },
  { value: "distrito", label: "Distrito" },
  { value: "marca", label: "Marca" },
  { value: "categoria", label: "Categoría" },
  { value: "proveedor", label: "Proveedor" },
];

/**
 * Calcula las fechas por defecto: últimos 12 meses.
 */
function getDefaultDates() {
  const now = new Date();
  const fechaFin = now.toISOString().split("T")[0];
  const past = new Date(now);
  past.setMonth(past.getMonth() - 12);
  const fechaInicio = past.toISOString().split("T")[0];
  return { fechaInicio, fechaFin };
}

/**
 * Calcula la fecha mínima permitida (24 meses atrás desde hoy).
 */
function getMinDate() {
  const now = new Date();
  const past = new Date(now);
  past.setMonth(past.getMonth() - 24);
  return past.toISOString().split("T")[0];
}

export function AperturaPage() {
  const dispatch = useDispatch();
  const { data, top20Productos, totales, loading, error, filters } =
    useSelector((state) => state.bi.apertura);
  const token = useSelector((state) => state.app.user?.token);

  // Dynamic filter options
  const [marcaOptions, setMarcaOptions] = useState([]);

  // Fetch marca options on mount
  useEffect(() => {
    if (!token) return;
    const headers = { "Content-type": "application/json", Authorization: `Bearer ${token}` };
    fetch(`${baseUrl}/marcas`, { headers })
      .then((res) => res.ok ? res.json() : [])
      .then((data) =>
        setMarcaOptions(
          data
            .map((m) => ({ value: m.nombre, label: m.nombre }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch(() => {});
  }, [token]);

  // Inicializar fechas por defecto si no están seteadas
  useEffect(() => {
    if (!filters.fechaInicio || !filters.fechaFin) {
      const { fechaInicio, fechaFin } = getDefaultDates();
      dispatch(setAperturaFilters({ fechaInicio, fechaFin }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Función para fetching de datos
  const doFetch = useCallback(() => {
    return dispatch(fetchApertura());
  }, [dispatch]);

  // Polling cada 15 min (datos pre-calculados diariamente, pero polling mantiene freshness)
  usePolling(doFetch, 900000, true);

  // Re-fetch cuando cambian los filtros
  useEffect(() => {
    if (filters.fechaInicio && filters.fechaFin) {
      dispatch(fetchApertura());
    }
  }, [dispatch, filters]);

  // Handler para cambios en filtros de la FilterBar
  const handleFilterChange = useCallback(
    (name, value) => {
      dispatch(setAperturaFilters({ [name]: value || null }));
    },
    [dispatch]
  );

  // Handler para cambio de agrupación
  const handleAgrupacionChange = useCallback(
    (e) => {
      dispatch(setAperturaFilters({ agruparPor: e.target.value }));
    },
    [dispatch]
  );

  // Handler para limpiar filtros
  const handleClearFilters = useCallback(() => {
    dispatch(clearAperturaFilters());
    const { fechaInicio, fechaFin } = getDefaultDates();
    dispatch(setAperturaFilters({ fechaInicio, fechaFin }));
  }, [dispatch]);

  // URL de exportación con filtros actuales
  const exportUrl = useMemo(() => {
    const params = Object.entries(filters)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");
    return `${baseUrl}/bi/apertura/export/excel${params ? `?${params}` : ""}`;
  }, [filters]);

  // Configuración de filtros para FilterBar
  const filterConfig = useMemo(
    () => [
      {
        name: "fechaInicio",
        label: "Fecha Inicio",
        type: "date",
        min: getMinDate(),
      },
      {
        name: "fechaFin",
        label: "Fecha Fin",
        type: "date",
      },
      {
        name: "distrito",
        label: "Distrito",
        type: "select",
        options: [], // Se poblaría dinámicamente desde el backend
      },
      {
        name: "marca",
        label: "Marca",
        type: "select",
        options: marcaOptions,
      },
      {
        name: "categoria",
        label: "Categoría",
        type: "select",
        options: [], // Se poblaría dinámicamente desde el backend
      },
    ],
    [marcaOptions]
  );

  // Columnas de la tabla principal de agrupación
  const mainColumns = useMemo(
    () => [
      {
        key: "nombreAgrupacion",
        label:
          AGRUPACION_OPTIONS.find((o) => o.value === filters.agruparPor)
            ?.label || "Agrupación",
        sortable: true,
      },
      {
        key: "ventasTotales",
        label: "Ventas Totales (₡)",
        sortable: true,
        render: (row) => formatColones(row.ventasTotales),
      },
      {
        key: "unidadesVendidas",
        label: "Unidades Vendidas",
        sortable: true,
        render: (row) =>
          Number(row.unidadesVendidas).toLocaleString("es-CR"),
      },
      {
        key: "cantidadOrdenes",
        label: "Cantidad Órdenes",
        sortable: true,
        render: (row) =>
          Number(row.cantidadOrdenes).toLocaleString("es-CR"),
      },
      {
        key: "ticketPromedio",
        label: "Ticket Promedio (₡)",
        sortable: true,
        render: (row) => formatColones(row.ticketPromedio),
      },
      {
        key: "participacionPorcentual",
        label: "Participación %",
        sortable: true,
        render: (row) => formatPorcentaje(row.participacionPorcentual),
      },
    ],
    [filters.agruparPor]
  );

  // Columnas de la tabla Top 20 productos
  const top20Columns = useMemo(
    () => [
      {
        key: "ranking",
        label: "#",
      },
      {
        key: "nombreProducto",
        label: "Producto",
      },
      {
        key: "marca",
        label: "Marca",
      },
      {
        key: "unidadesVendidas",
        label: "Unidades Vendidas",
        render: (row) =>
          Number(row.unidadesVendidas).toLocaleString("es-CR"),
      },
      {
        key: "ventasTotales",
        label: "Ventas Totales (₡)",
        render: (row) => formatColones(row.ventasTotales),
      },
    ],
    []
  );

  // Preparar datos de top20 con ranking index
  const top20Data = useMemo(
    () =>
      (top20Productos || []).map((item, index) => ({
        ...item,
        id: item.id || index,
        ranking: index + 1,
      })),
    [top20Productos]
  );

  const noData = !loading && data.length === 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xl font-bold">Reporte de Apertura</p>
        <ExportButtons
          exportUrl={exportUrl}
          label="Exportar Excel"
          disabled={loading || noData}
        />
      </div>

      {/* Selector de agrupación */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">
            Agrupar por:
          </label>
          <select
            value={filters.agruparPor || "provincia"}
            onChange={handleAgrupacionChange}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple focus:border-transparent"
          >
            {AGRUPACION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filtros */}
      <FilterBar
        filters={filterConfig}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Totales summary */}
      {totales && !noData && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium">
                Ventas Totales
              </p>
              <p className="text-lg font-bold text-gray-800">
                {formatColones(totales.ventasTotales)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium">
                Unidades Vendidas
              </p>
              <p className="text-lg font-bold text-gray-800">
                {Number(totales.unidadesVendidas || 0).toLocaleString("es-CR")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje cuando no hay datos */}
      {noData && !error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-700">
            No hay datos disponibles para la selección actual. Intente ajustar
            los filtros o el rango de fechas.
          </p>
        </div>
      )}

      {/* Tabla principal de agrupaciones */}
      <div className="mb-8">
        <p className="text-lg font-semibold mb-3">
          Métricas por{" "}
          {AGRUPACION_OPTIONS.find((o) => o.value === filters.agruparPor)
            ?.label || "Agrupación"}
        </p>
        <DataTable
          columns={mainColumns}
          data={data}
          loading={loading}
          emptyMessage="No hay datos para la selección actual."
        />
      </div>

      {/* Tabla Top 20 productos */}
      <div>
        <p className="text-lg font-semibold mb-3">
          Top 20 Productos Más Vendidos
        </p>
        <DataTable
          columns={top20Columns}
          data={top20Data}
          loading={loading}
          emptyMessage="No hay datos de productos para la selección actual."
        />
      </div>
    </div>
  );
}

export default AperturaPage;
